import React from 'react'
import { observer } from 'mobx-react-lite'
import PropTypes from 'prop-types'
import { Select } from 'antd'
import _ from 'lodash'
/**
 * OptionsSelector is a styled wrapper around Ant Design's Select component,
 * providing optional sorting, custom dropdown dimensions, and default styling.
 *
 * @component
 * @param {Object[]} options                     – Array of option objects: { key, label, value }.
 * @param {string|number|Object|Array} value     – Currently selected value(s).
 * @param {Function} onChange                    – Callback invoked when selection changes.
 * @param {'multiple'|'tags'} [mode]             – Select mode for multiple or tag entry.
 * @param {boolean} [sort=false]                 – If true, sort options alphabetically by label.
 * @param {string} [placeholder='--']            – Placeholder text when no value is selected.
 * @param {Object} [style]                       – Inline styles for the Select component.
 * @param {string} [className]                   – Additional CSS class names.
 * @param {number} [listHeight=150]              – Height of the dropdown list.
 * @param {number} [listItemHeight=10]           – Height of each dropdown item.
 * @param {Object} [dropdownStyle]               – Additional styles for the dropdown menu.
 * @param {string} [dropdownMinWidth='200px']    – Minimum width for the dropdown.
 * @param {...Object} rest                       – Any other Ant Design Select props.
 * @returns {JSX.Element}
 */
const OptionsSelector = ({ options, value, onChange, mode, ...props }) => {
	const {
		style,
		className,
		placeholder,
		listHeight,
		listItemHeight,
		dropdownStyle,
		sort = false,
		dropdownMinWidth = false,
		...rest
	} = props

	const sortedOptions =
		sort &&
		_.orderBy(options, (o) => {
			if (typeof o.label === 'string') {
				// Defined locally such as placeholder, or extra options
				return o.label.toLowerCase()
			}
			if (typeof o.label === 'object') {
				return o.label.props.children.toLowerCase()
			}

			return o.label
		})

	const minWidth = dropdownMinWidth
		? { minWidth: dropdownMinWidth }
		: { minWidth: '200px' }

	return (
		<Select
			allowClear
			options={sort ? sortedOptions : options}
			value={value}
			onChange={onChange}
			placement="bottomLeft"
			placeholder={placeholder ?? '--'}
			mode={mode}
			className={`${
				className ?? 'antd-select-style-1 '
			} we-options-selector`}
			listHeight={listHeight ?? 150}
			listItemHeight={listItemHeight ?? 10}
			style={{
				...{ width: '100%', display: 'inline-block' },
				...style,
				...minWidth
			}}
			dropdownStyle={{ ...minWidth, ...dropdownStyle }}
			{...rest}
		/>
	)
}

OptionsSelector.propTypes = {
	options: PropTypes.array,
	mode: PropTypes.string,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.array,
		PropTypes.number
	]),
	onChange: PropTypes.func,
	style: PropTypes.object,
	className: PropTypes.string,
	placeholder: PropTypes.string,
	listHeight: PropTypes.number,
	listItemHeight: PropTypes.number,
	dropdownStyle: PropTypes.object,
	dropdownMinWidth: PropTypes.string,
	sort: PropTypes.bool
}

export default observer(OptionsSelector)
